import React, { useState } from 'react';
import '../styles/Auth.css';
import { signup, login, forgotPassword } from '../api/auth';
import OAuthButtons from './OAuthButtons';
import { INDUSTRIES } from '../constants/industries';

function Auth({ onLogin }) {
  const [mode, setMode] = useState('login'); // 'login' or 'signup'
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  const [employer, setEmployer] = useState('');
  const [industry, setIndustry] = useState('');
  const [error, setError] = useState('');
  const [notice, setNotice] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setNotice('');
    setLoading(true);

    try {
      if (mode === 'forgot') {
        const res = await forgotPassword(email);
        setNotice(res.message || 'If that email is registered, a reset link is on its way.');
      } else if (mode === 'login') {
        const response = await login(email, password);
        onLogin(response.user, response.token);
      } else {
        const response = await signup(email, password, name, employer, industry);
        onLogin(response.user, response.token);
      }
    } catch (err) {
      setError(err.message || 'Authentication failed');
    } finally {
      setLoading(false);
    }
  };

  const handleOAuthSuccess = (userData, token) => {
    onLogin(userData, token);
  };

  return (
    <div className="auth-container">
      <div className="auth-card">
        <h2>{mode === 'login' ? 'Welcome Back' : mode === 'signup' ? 'Join ConnectMi' : 'Reset your password'}</h2>
        {mode === 'forgot' && (
          <p className="auth-sub">Enter your email and we'll send you a reset link.</p>
        )}

        <form onSubmit={handleSubmit} className="auth-form">
          {mode === 'signup' && (
            <>
              <div className="form-group">
                <label htmlFor="name">Full Name</label>
                <input
                  id="name"
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Your name"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="employer">Employer</label>
                <input
                  id="employer"
                  type="text"
                  value={employer}
                  onChange={(e) => setEmployer(e.target.value)}
                  placeholder="Company name, or Freelancer / Own business"
                  required
                />
              </div>
              <div className="form-group">
                <label htmlFor="industry">Industry</label>
                <select
                  id="industry"
                  value={industry}
                  onChange={(e) => setIndustry(e.target.value)}
                  required
                >
                  <option value="" disabled>Select your industry…</option>
                  {INDUSTRIES.map((ind) => (
                    <option key={ind} value={ind}>{ind}</option>
                  ))}
                </select>
              </div>
            </>
          )}

          <div className="form-group">
            <label htmlFor="email">Email</label>
            <input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="your@email.com"
              required
            />
          </div>

          {mode !== 'forgot' && (
            <div className="form-group">
              <label htmlFor="password">Password</label>
              <input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder={mode === 'signup' ? 'At least 8 chars, a letter and a number' : '••••••••'}
                required
              />
              {mode === 'login' && (
                <button
                  type="button"
                  className="link-button forgot-link"
                  onClick={() => { setMode('forgot'); setError(''); setNotice(''); }}
                >
                  Forgot password?
                </button>
              )}
            </div>
          )}

          {error && <div className="error-message">{error}</div>}
          {notice && <div className="send-notice">{notice}</div>}

          <button type="submit" className="btn btn-primary" disabled={loading}>
            {loading ? 'Processing...' : mode === 'login' ? 'Sign In' : mode === 'signup' ? 'Create Account' : 'Send reset link'}
          </button>
        </form>

        {mode !== 'forgot' && (
          <>
            <div className="auth-divider">
              <span>Or continue with</span>
            </div>
            <OAuthButtons onSuccess={handleOAuthSuccess} />
          </>
        )}

        <div className="auth-toggle">
          {mode === 'forgot' ? (
            <button
              type="button"
              className="link-button"
              onClick={() => { setMode('login'); setError(''); setNotice(''); }}
            >
              Back to sign in
            </button>
          ) : mode === 'login' ? (
            <>
              Don't have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setMode('signup');
                  setError('');
                }}
              >
                Sign up
              </button>
            </>
          ) : (
            <>
              Already have an account?{' '}
              <button
                type="button"
                className="link-button"
                onClick={() => {
                  setMode('login');
                  setError('');
                }}
              >
                Sign in
              </button>
            </>
          )}
        </div>
      </div>

      <div className="auth-info">
        <h3>🎯 Why ConnectMi?</h3>
        <ul>
          <li>✅ <strong>Verified connections</strong> - Only mutual trust relationships</li>
          <li>✅ <strong>Path visualization</strong> - See how you're connected to anyone</li>
          <li>✅ <strong>Privacy first</strong> - Your contact list stays private</li>
          <li>✅ <strong>No algorithms</strong> - Pure trust, no manipulation</li>
        </ul>
      </div>
    </div>
  );
}

export default Auth;
